import { Container, FillGradient, Graphics, Sprite, Texture } from 'pixi.js';
import { MOON } from './NightSky';

// Moonbeam lighting for The Pit night mode: a light cone falling from the
// moon onto the floor. The hole and the moon are real sprites now
// (area_top_hole.png + NightSky.ts); this module only paints the light.

// Beam mouth anchored AT the moon (single source of truth - move the moon and
// the beam follows). Half-width roughly matches the moon disc; the fade-in
// ramp below hides the hard top edge so the light melts out of the moon.
const BEAM_TOP_X = MOON.x;
const BEAM_TOP_Y = MOON.y;
const BEAM_TOP_HALF_WIDTH = 50;

// Moonbeam: a cone from the moon down-left onto the floor, plus an elliptic
// light pool where it lands. Additive, low alpha - reads as light, not as
// paint.
// Moonlight color (0xcfe4ff) as rgba - gradient stops carry alpha per stop.
function beamRgba(alpha: number): string {
  return `rgba(207,228,255,${alpha})`;
}
// Peak alpha on the beam's center line before the length fade (the old
// layered-quads stack summed to the same 8 * 0.02).
const BEAM_PEAK_ALPHA = 0.16;
// Flat-bright core: inside this fraction of the local half-width the cross
// falloff stays at 1, then fades linearly to 0 at the edge. Kept wide: a
// narrow core reads as a visible bright strip where the beam meets the floor.
const CORE_FRAC = 0.35;
// Length fade (bright at the moon -> gone at the floor), so the cone melts
// into the radial pool instead of slamming a hard-edged stripe onto it.
// Offsets are fractions of the beam length. FADE_IN ramps the alpha up from 0
// at the very top so the mouth has no hard horizontal edge.
const BEAM_FADE_IN = 0.15;
const BEAM_FADE_MID = 0.7;
const BEAM_FADE_MID_MUL = 0.4;
// Supersample factor for the bake - keeps the dither grain sub-pixel after
// the design -> screen upscale.
const BAKE_SCALE = 2;
// Pool pulled toward the arena center so the beam slants hard down-left from
// the hole (moonlight raking across the arena, not dropping straight down).
const POOL_X = 400;
const POOL_Y = 335;
const POOL_RX = 237;
const POOL_RY = 40;
// Radial-gradient pool: alpha at the center, fading to 0 at the rim.
const POOL_CENTER_ALPHA = 0.3;

// Piecewise-linear length fade along the beam (t: 0 = mouth, 1 = floor).
// Same stops the old gradient used: ramp in, dim past the midpoint, gone at
// the floor.
function lengthFade(t: number): number {
  if (t < BEAM_FADE_IN) return t / BEAM_FADE_IN;
  if (t < BEAM_FADE_MID) {
    const k = (t - BEAM_FADE_IN) / (BEAM_FADE_MID - BEAM_FADE_IN);
    return 1 + (BEAM_FADE_MID_MUL - 1) * k;
  }
  return BEAM_FADE_MID_MUL * (1 - (t - BEAM_FADE_MID) / (1 - BEAM_FADE_MID));
}

// Bakes the whole cone into ONE canvas texture, computed per pixel with
// randomized rounding on the alpha (dither). Gradient fills - GPU and canvas
// alike - quantize these near-zero alphas to a handful of 8-bit steps that
// read as horizontal bands across the beam; the dither trades the steps for
// invisible noise. Baked once per mount at BAKE_SCALE x design resolution.
function buildBeamTexture(): { texture: Texture; x: number; y: number; scale: number } {
  const topY = BEAM_TOP_Y;
  const left = Math.floor(Math.min(POOL_X - POOL_RX, BEAM_TOP_X - BEAM_TOP_HALF_WIDTH));
  const right = Math.ceil(Math.max(POOL_X + POOL_RX, BEAM_TOP_X + BEAM_TOP_HALF_WIDTH));
  const width = (right - left) * BAKE_SCALE;
  const height = Math.ceil((POOL_Y - topY) * BAKE_SCALE);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(width, height);
  const data = img.data;

  for (let py = 0; py < height; py++) {
    const t = py / (height - 1);
    // Center line and half-width interpolate from the mouth to the pool.
    const centerX = BEAM_TOP_X + (POOL_X - BEAM_TOP_X) * t;
    const halfWidth = BEAM_TOP_HALF_WIDTH + (POOL_RX - BEAM_TOP_HALF_WIDTH) * t;
    const rowAlpha = BEAM_PEAK_ALPHA * lengthFade(t);
    if (rowAlpha <= 0) continue;
    for (let px = 0; px < width; px++) {
      const designX = left + px / BAKE_SCALE;
      const edgeDist = Math.abs(designX - centerX) / halfWidth;
      if (edgeDist >= 1) continue;
      const cross = edgeDist <= CORE_FRAC ? 1 : (1 - edgeDist) / (1 - CORE_FRAC);
      // Randomized rounding: the fractional part of the ideal 8-bit alpha
      // becomes the probability of rounding up - noise instead of bands.
      const alpha8 = Math.floor(rowAlpha * cross * 255 + Math.random());
      if (alpha8 <= 0) continue;
      const i = (py * width + px) * 4;
      data[i] = 207;
      data[i + 1] = 228;
      data[i + 2] = 255;
      data[i + 3] = alpha8;
    }
  }

  ctx.putImageData(img, 0, 0);
  return { texture: Texture.from(canvas), x: left, y: topY, scale: 1 / BAKE_SCALE };
}

// Moonbeam + floor pool. Mount AFTER arena-bottom so the light spills over the
// floor art (still behind the fighters - it lies on the ground).
// getBobOffset (optional): sampled every frame; the beam translates by it so
// the light sways together with the bobbing moon. Only the cone moves - its
// floor end fades to zero alpha so the shift is invisible there, and the pool
// stays put on the ground.
export function buildMoonBeam(getBobOffset?: () => number): Container {
  const group = new Container();

  const baked = buildBeamTexture();
  const beam = new Sprite(baked.texture);
  beam.position.set(baked.x, baked.y);
  beam.scale.set(baked.scale);
  beam.blendMode = 'add';
  if (getBobOffset) {
    beam.onRender = () => {
      beam.y = baked.y + getBobOffset();
    };
  }
  group.addChild(beam);

  // Floor pool: radial gradient so it melts into the dark floor with no rim.
  const poolGradient = new FillGradient({
    type: 'radial',
    center: { x: 0.5, y: 0.5 },
    innerRadius: 0,
    outerCenter: { x: 0.5, y: 0.5 },
    outerRadius: 0.5,
    colorStops: [
      { offset: 0, color: beamRgba(POOL_CENTER_ALPHA) },
      { offset: 1, color: beamRgba(0) },
    ],
    textureSpace: 'local',
  });
  const pool = new Graphics()
    .ellipse(POOL_X, POOL_Y, POOL_RX, POOL_RY)
    .fill(poolGradient);
  pool.blendMode = 'add';
  group.addChild(pool);

  return group;
}
